/**
 * Resolves og:image / twitter:image against the ACTUAL running host, at request time.
 *
 * nuxt.config.ts's static `app.head` entries (AlexAmin/marcus#214, first attempt) computed this
 * from `process.env.APP_PUBLIC_URL` inside nuxt.config.ts itself — a BUILD-time evaluation. That
 * failed for two independent reasons, both confirmed against the deployed services:
 *
 *   1. Wrong var: the deployed services set `NUXT_APP_PUBLIC_URL` (Nitro's env-var override
 *      convention for `runtimeConfig.appPublicUrl`), never the bare `APP_PUBLIC_URL` nuxt.config.ts
 *      read directly.
 *   2. Wrong time — the load-bearing one. `Dockerfile`'s BUILD stage runs `pnpm --filter web build`,
 *      which is where nuxt.config.ts's `app.head` array gets evaluated and compiled into the app's
 *      output; the runtime stage only declares `ARG APP_VERSION` / `ARG BUILD_TIME`. So nothing set
 *      on the running Cloud Run service — a Terraform-managed env var included — can reach a value
 *      computed inside nuxt.config.ts: it is already frozen into the compiled HTML by the time the
 *      service starts. Setting `NUXT_APP_PUBLIC_URL` on the service would not have fixed this on its
 *      own, even with the var name corrected.
 *
 * This plugin fixes both: `useRuntimeConfig()` is a RUNTIME lookup that already honours Nitro's
 * `NUXT_APP_PUBLIC_URL` override, and — because Nuxt plugins re-run per request during SSR — it
 * re-resolves on every request from the one shared container, rather than once at build time.
 *
 * Server-only on purpose. `runtimeConfig.appPublicUrl` (nuxt.config.ts, near the top-level
 * `runtimeConfig` block) is a PRIVATE key — never sent to the client — and there is no reason to
 * change that just for this: og:image/twitter:image exist for scrapers, which only ever fetch
 * server-rendered HTML and never execute client JS. A public runtimeConfig key would ship the
 * hostname to the browser for zero benefit.
 *
 * The `key`s below match nuxt.config.ts's static og:image/twitter:image meta entries exactly —
 * unhead dedupes by `key`, so this REPLACES those entries rather than appending duplicates. Same
 * mechanism `faviconEnv.client.ts` uses for the favicon, and `auth-ssr.ts` for the
 * `if (!import.meta.server) return` server-only guard (the inverse guard, here).
 */
export default defineNuxtPlugin(() => {
  if (!import.meta.server) return

  const config = useRuntimeConfig()
  const origin = ((config.appPublicUrl as string | undefined) || 'https://holamarcus.com').replace(/\/+$/, '')

  useHead({
    meta: [
      { key: 'og-image', property: 'og:image', content: `${origin}/og-image.png?v=3` },
      { key: 'twitter-image', name: 'twitter:image', content: `${origin}/og-image.png?v=3` },
    ],
  })
})
